// src/pages/bloomterminal/components/dashboard/WidgetContainer.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart4, GripVertical, X } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import RestrictedChartOverlay from './RestrictedChartOverlay';

/**
 * WidgetContainer - Wrapper component for chart widgets
 *
 * Features:
 * - Minimal/borderless design matching dark theme
 * - Hover controls: drag handle and close button
 * - Smooth animations with Framer Motion
 * - Renders the chart's containerComponent which handles its own data fetching
 * - When locked (tier): shows registry preview image only — no chart mount, no API data in DOM
 */
const WidgetContainer = ({ chartConfig, apiParams, onRemove, locked = false, showControls = true }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const [isHovered, setIsHovered] = useState(false);

  if (!chartConfig) {
    return (
      <div
        className="h-full w-full flex items-center justify-center rounded-lg"
        style={{ backgroundColor: theme.widgetBg }}
      >
        <p className="text-gray-500">{t('charts.notFound')}</p>
      </div>
    );
  }

  const ChartContainer = chartConfig.containerComponent;

  return (
    <div
      className={
        locked
          ? 'relative flex h-full min-h-0 w-full flex-col bg-transparent'
          : 'flex h-full min-h-0 min-w-0 w-full flex-col bg-transparent'
      }
      onMouseEnter={() => (showControls ? setIsHovered(true) : undefined)}
      onMouseLeave={() => (showControls ? setIsHovered(false) : undefined)}
    >
      {/* Hover Controls */}
      <AnimatePresence>
        {showControls && isHovered && (
          <motion.div
            className={`absolute top-2 right-2 flex items-center gap-1 ${locked ? 'z-50' : 'z-20'}`}
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.15 }}
          >
            {/* Drag Handle */}
            <div
              className="widget-drag-handle cursor-grab active:cursor-grabbing p-1.5 rounded transition-colors"
              style={{ backgroundColor: theme.widgetControlBg }}
              title={t('charts.dragToMove')}
            >
              <GripVertical size={16} className="text-[#828382] dark:text-gray-400" />
            </div>

            {/* Close Button */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRemove?.();
              }}
              type="button"
              className="p-1.5 rounded hover:bg-red-900/70 transition-colors"
              style={{ backgroundColor: theme.widgetControlBg }}
              title={t('charts.removeWidget')}
            >
              <X size={16} className="text-[#828382] dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {locked ? (
        <div className="widget-chart-content relative flex h-full min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-b-lg bg-white dark:bg-[#191919] pointer-events-none">
          {chartConfig.previewImage ? (
            <img
              src={chartConfig.previewImage}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          ) : (
            <div className="relative z-0 flex h-full w-full items-center justify-center">
              <BarChart4 size={48} className="text-[#828382] dark:text-gray-600" aria-hidden />
            </div>
          )}
        </div>
      ) : (
        <div className="widget-chart-content h-full w-full min-h-0">
          <ChartContainer apiParams={apiParams} />
        </div>
      )}
      {locked && <RestrictedChartOverlay />}
    </div>
  );
};

export default WidgetContainer;
